import { loginSchema } from "@reelio/contracts";
import { useState, type FormEvent } from "react";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { authClient } from "./auth-client";
import { getAuthErrorMessage } from "./auth-errors";
import { useAuth } from "./AuthProvider";
import { AuthShell } from "./AuthShell";
import { Field, PrimaryButton } from "./FormControls";
import { getSafeRedirect } from "./redirect";

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const signupPath = `/auth/signup${location.search}`;
  const forgotPath = `/auth/forgot-password${location.search}`;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = loginSchema.safeParse({
      email,
      password,
      keepSignedIn,
    });

    if (!parsed.success) {
      const fieldErrors = parsed.error.flatten().fieldErrors;
      setErrors({
        email: fieldErrors.email?.[0],
        password: fieldErrors.password?.[0],
      });
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setFormError("");

    try {
      const result = await authClient.signIn.email({
        email: parsed.data.email,
        password: parsed.data.password,
        rememberMe: parsed.data.keepSignedIn,
      });

      if (result.error) {
        setFormError(
          getAuthErrorMessage(
            result.error,
            "Reelio could not sign you in. Try again.",
          ),
        );
        return;
      }

      await refresh();
      navigate(getSafeRedirect(location.search), { replace: true });
    } catch (error) {
      setFormError("Reelio could not sign you in. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Log in to Reelio"
      footer={
        <p>
          Don&apos;t have an account? <Link to={signupPath}>Sign up</Link>
        </p>
      }
    >
      <form className="auth-form" onSubmit={submit} noValidate>
        <Field
          label="Email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@yourbrand.com"
          value={email}
          error={errors.email}
          onChange={(event) => {
            setEmail(event.target.value);
            setErrors((current) => ({ ...current, email: undefined }));
            setFormError("");
          }}
        />

        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          placeholder="Your password"
          value={password}
          error={errors.password}
          onChange={(event) => {
            setPassword(event.target.value);
            setErrors((current) => ({
              ...current,
              password: undefined,
            }));
            setFormError("");
          }}
        />

        <div className="auth-form__options">
          <label className="auth-checkbox">
            <input
              type="checkbox"
              checked={keepSignedIn}
              onChange={(event) => setKeepSignedIn(event.target.checked)}
            />
            <span>Keep me signed in</span>
          </label>
          <Link to={forgotPath}>Forgot password?</Link>
        </div>

        {formError ? (
          <p className="auth-form__alert" role="alert">
            {formError}
          </p>
        ) : null}

        <PrimaryButton
          type="submit"
          isLoading={isSubmitting}
          loadingLabel="Signing in…"
        >
          Log in
        </PrimaryButton>
      </form>
    </AuthShell>
  );
};
